import React from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import TCE from "./frontend/pages/TCE/index.js";
import TCA from "./frontend/pages/TCA/index.js";
import TcaCieemgEmpregador from "./frontend/pages/TcaEmpregador/index.js";
import Inicial from "./frontend/pages/Inicial/index.js";
import Dashboard from "./frontend/pages/Dashboard/index.js";
import Sidebar from "./frontend/components/Sidebar/index.js";
import Header from "./frontend/components/Header/index.js";
import TcaConfiguracoes from "./frontend/pages/TcaConfiguracoes/index.js";
import Acompanhamento from "./frontend/pages/Acompanhamento/index.js";
import Validacoes from "./frontend/pages/Validacoes/index.js";
import HistoricoEventos from "./frontend/pages/HistoricoEventos/index.js";

function Layout() {
  const location = useLocation();
  const hideLayoutRoutes = ["/"];

  const hideLayout = hideLayoutRoutes.includes(location.pathname);

  return (
    <>
      {!hideLayout && <Header />}
      {!hideLayout && <Sidebar />}
      <Outlet />
    </>
  );
}

// Guard: empresa só acessa o programa dela
function EmpresaRouteGuard({ children, programaPermitido }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      const isEmpresa =
        decoded.tipo === "empresa" || decoded.tipo === "usuario_externo";

      // Estudante (nível 1) tem acesso à autorização vinculada ao seu CPF,
      // aberta via ?id=…) — seja TCE (/tce), TCA (/tca) ou TCA-Empregador
      // (/tca-empregador) — e aos menus transversais "Acompanhamento" e
      // "Histórico", que listam todas as autorizações vinculadas ao CPF dele
      // de forma individual.
      const isEstudante =
        decoded.tipo === "estudante" ||
        Number(decoded.acesso_autorizacao) === 1;

      if (isEstudante) {
        // Mapa de programas do estudante: define qual rota casa com
        // cada tipo_programa. Sem isso, o aprendiz (tipo_programa=1)
        // seria redirecionado pra /tce, que é o caminho do estagiário.
        const mapaEstudante = {
          0: "/tce",
          1: "/tca",
          2: "/tca-empregador",
        };
        const rotaDoPrograma =
          mapaEstudante[Number(decoded.tipo_programa)] ?? null;

        const rotaLiberada =
          location.pathname.startsWith("/tce") ||
          location.pathname.startsWith("/tca") ||
          location.pathname.startsWith("/tca-empregador") ||
          location.pathname.startsWith("/acompanhamento") ||
          location.pathname.startsWith("/historico-eventos");

        if (!rotaLiberada) {
          // Se não está numa rota liberada, redireciona pra rota do
          // programa dele (se conhecida) ou, em último caso, pra /tce.
          // IMPORTANTE: nunca mais redirecionar o aprendiz pra /tce
          // quando o tipo_programa for 1 ou 2 — isso é o bug que estava
          // jogando o aprendiz de TCA na tela "Nenhuma autorização
          // vinculada".
          navigate(rotaDoPrograma ?? "/tce", { replace: true });
        }
        return;
      }

      if (!isEmpresa) return;

      const tipoPrograma = Number(decoded.tipo_programa);

      // Mapa de programas permitidos
      const mapa = {
        0: ["/tce"],
        1: ["/tca"],
        2: ["/tca-empregador"],
      };

      const permitidas = mapa[tipoPrograma] || [];
      if (permitidas.length === 0) return;

      if (!permitidas.some((p) => location.pathname.startsWith(p))) {
        navigate(permitidas[0], { replace: true });
      }
    } catch (e) {
      console.error("Erro no EmpresaRouteGuard:", e);
    }
  }, [location.pathname, navigate]);

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Inicial />} />
        <Route element={<Layout />}>
          <Route
            path="/tce"
            element={
              <EmpresaRouteGuard programaPermitido={0}>
                <TCE />
              </EmpresaRouteGuard>
            }
          />
          <Route
            path="/tca"
            element={
              <EmpresaRouteGuard programaPermitido={1}>
                <TCA />
              </EmpresaRouteGuard>
            }
          />
          <Route
            path="/tca-empregador"
            element={
              <EmpresaRouteGuard programaPermitido={2}>
                <TcaCieemgEmpregador />
              </EmpresaRouteGuard>
            }
          />
          <Route path="/tca-configuracoes" element={<TcaConfiguracoes />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/acompanhamento" element={<Acompanhamento />} />
          <Route path="/validacoes" element={<Validacoes />} />
          <Route path="/historico-eventos" element={<HistoricoEventos />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
